/**
 * PhotoStatusOverlay - Status indicator for individual photos
 * 
 * Shows processing status overlaid on photo thumbnails:
 * - Darkened overlay with spinner for pending/processing
 * - Warning icon for failed
 * - Completed photos show no overlay
 * 
 * Design inspired by PhotoRenderStatus but simpler/smaller for thumbnails.
 */

export class PhotoStatusOverlay {
  /**
   * Create overlay HTML for a photo based on its processing status
   * @param {string} status - Photo processing status: 'pending', 'processing', 'completed', 'failed'
   * @param {boolean} hasThumb - Whether the photo has a thumbnail URL available (optional)
   * @returns {string} HTML string for the overlay (empty if completed or has thumbnail)
   */
  static createOverlayHTML(status, hasThumb = false) {
    // If photo has a thumbnail, don't show spinner even if still processing embeddings
    // The image is viewable, so no need to block the UI
    if (status === 'completed' || hasThumb) {
      return ''; // No overlay for completed photos or photos with thumbnails
    }
    
    if (status === 'failed') {
      return `
        <div class="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded">
          <div class="text-center">
            <svg class="w-8 h-8 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <div class="text-xs text-red-400 mt-1">Failed</div>
          </div>
        </div>
      `;
    }
    
    // pending or processing (but no thumbnail yet)
    return `
      <div class="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center rounded">
        <svg class="animate-spin h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `;
  }
  
  /**
   * Update an existing photo element's overlay based on status
   * @param {HTMLElement} photoElement - The photo div element
   * @param {string} status - New processing status
   * @param {boolean} hasThumb - Whether the photo has a thumbnail URL available (optional)
   */
  static updatePhotoOverlay(photoElement, status, hasThumb = false) {
    if (!photoElement) return;
    
    // Remove existing overlay if any
    const existingOverlay = photoElement.querySelector('.absolute.inset-0');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Add new overlay if needed
    const overlayHTML = PhotoStatusOverlay.createOverlayHTML(status, hasThumb);
    if (overlayHTML) {
      photoElement.insertAdjacentHTML('beforeend', overlayHTML);
    }
  }
  
  /**
   * Calculate folder processing statistics
   * @param {Array} photos - Array of photo objects with processing_status
   * @returns {Object} { total, completed, failed, processing, hasProcessing }
   */
  static getFolderProcessingStats(photos) {
    if (!photos || photos.length === 0) {
      return { total: 0, completed: 0, failed: 0, processing: 0, hasProcessing: false };
    }
    
    const total = photos.length;
    let completed = 0;
    let failed = 0;
    let processing = 0;
    
    photos.forEach(photo => {
      if (photo.processing_status === 'completed') {
        completed++;
      } else if (photo.processing_status === 'failed') {
        failed++;
      } else if (photo.processing_status === 'processing' || photo.processing_status === 'pending') {
        processing++;
      }
    });
    
    return {
      total,
      completed,
      failed,
      processing,
      hasProcessing: processing > 0
    };
  }
  
  /**
   * Create HTML for folder processing progress bar
   * @param {Object} stats - Processing stats from getFolderProcessingStats
   * @param {boolean} isActive - Whether this folder is currently active
   * @returns {string} HTML string for progress bar
   */
  static createFolderProgressHTML(stats, isActive = false) {
    if (!stats.hasProcessing) {
      return ''; // No progress bar if nothing is processing
    }
    
    const ready = stats.completed + stats.failed;
    const percentage = Math.round((ready / stats.total) * 100);
    
    // Adjust colors based on whether folder is active
    const iconColor = isActive ? 'text-green-300' : 'text-green-400';
    const textColor = isActive ? 'text-blue-100' : 'text-gray-400';
    const borderColor = isActive ? 'border-white/20' : 'border-gray-700';
    const bgColor = isActive ? 'bg-white/20' : 'bg-gray-700';
    const barColor = isActive ? 'bg-gradient-to-r from-green-400 to-green-300' : 'bg-gradient-to-r from-green-500 to-green-400';
    
    return `
      <div class="mt-2.5 pt-2 border-t ${borderColor} text-xs">
        <div class="flex items-center justify-between mb-1.5">
          <div class="flex items-center space-x-1.5">
            <svg class="w-3.5 h-3.5 ${iconColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            <span class="${textColor}">Processing...</span>
          </div>
          <span class="${textColor} font-medium">${ready}/${stats.total}</span>
        </div>
        <div class="w-full ${bgColor} rounded-full h-2 overflow-hidden shadow-inner">
          <div class="${barColor} h-2 rounded-full transition-all duration-300 relative overflow-hidden shadow-sm" style="width: ${percentage}%">
            <div class="shimmer-effect"></div>
          </div>
        </div>
      </div>
    `;
  }
}
